import { useEffect, useId, useState, type ReactNode } from "react";
import type { GuestNoun } from "@vertu/shared";
import { summaryBarText } from "../../state/checkout-steps";
import { IMG_WIDTHS, optimizedImageUrl, optimizedSrcSet } from "../../lib/image";

/** One cart line as the summary lists it. */
export type SummaryLine = {
  key: string;
  title: string;
  /** "Thu 1 Oct · 08:30", or null. */
  when: string | null;
  /** "2 riders", or null (a line with no headcount). */
  party: string | null;
  /** Optional configured catalogue photo for this booking, never a generic filler. */
  imageUrl?: string;
};

function SummaryImage({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className="vt-checkout-summary-image"
      src={optimizedImageUrl(src, { width: 800 })}
      srcSet={optimizedSrcSet(src, IMG_WIDTHS.CARD)}
      sizes="(max-width: 640px) calc(100vw - 32px), 340px"
      alt=""
      width={600}
      height={360}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** One guest card as the summary lists it. */
export type SummaryRider = {
  key: string;
  /** "Rider 2" */
  title: string;
  /** What was typed for the name, or "". Never a private answer. */
  name: string;
  /** Saved: the server holds this rider as its card shows it (the dot filled). */
  saved: boolean;
  /** The card on screen now (the Riders step). */
  current: boolean;
};

/**
 * A promo code or gift card taken off the total, said where the total is:
 * what the visit comes to, what comes off and why, then what is due now.
 * Without it a guest from a campaign link saw a lower "Due now" than the
 * visit's price with nothing saying a code had been applied.
 */
export type SummaryDiscount = {
  /** "ZAR 5,600" */
  subtotal: string;
  /** "Discount · LAUNCH25" */
  label: string;
  /** "ZAR 1,400" (shown with a minus). */
  amount: string;
};

function Total({ dueLabel, due, discount }: { dueLabel: string; due: string; discount?: SummaryDiscount | null }) {
  return (
    <>
      {discount && (
        <div className="vt-checkout-summary-discounts">
          <div className="vt-checkout-summary-discount">
            <span>Subtotal</span>
            <span>{discount.subtotal}</span>
          </div>
          <div className="vt-checkout-summary-discount" data-kind="off">
            <span>{discount.label}</span>
            <span>−{discount.amount}</span>
          </div>
        </div>
      )}
      <div className="vt-summary vt-checkout-summary-total">
        <span>{dueLabel}</span>
        <strong>{due}</strong>
      </div>
    </>
  );
}

function Details({
  lines,
  riders,
  noun,
  dueLabel,
  due,
  discount = null,
  withTotal = true,
}: {
  lines: readonly SummaryLine[];
  riders: readonly SummaryRider[];
  noun: GuestNoun;
  dueLabel: string;
  due: string;
  discount?: SummaryDiscount | null;
  /** The total at the end (the summary column shows it with its actions instead). */
  withTotal?: boolean;
}) {
  const ridersTitle = noun.other.charAt(0).toUpperCase() + noun.other.slice(1);
  return (
    <>
      <ul className="vt-checkout-summary-lines" aria-label="Your visit">
        {lines.map((line) => (
          <li key={line.key} className="vt-checkout-summary-line">
            {line.imageUrl && <SummaryImage key={line.imageUrl} src={line.imageUrl} />}
            <span className="vt-checkout-summary-line-title">{line.title}</span>
            {(line.when || line.party) && (
              <span className="vt-checkout-summary-line-meta">
                {[line.when, line.party].filter(Boolean).join(" · ")}
              </span>
            )}
          </li>
        ))}
      </ul>
      {riders.length > 0 && (
        <div className="vt-checkout-summary-riders">
          <p className="vt-checkout-summary-heading">{ridersTitle}</p>
          <ul aria-label={ridersTitle}>
            {riders.map((rider) => (
              <li
                key={rider.key}
                className="vt-checkout-summary-rider"
                data-state={rider.saved ? "done" : "todo"}
                aria-current={rider.current ? "true" : undefined}
              >
                <span className="vt-checkout-summary-dot" aria-hidden="true" />
                <span className="vt-checkout-summary-rider-text">
                  {rider.title}
                  {rider.name ? ` · ${rider.name}` : ""}
                  <span className="vt-sr-only">{rider.saved ? ", saved" : ", not saved yet"}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {withTotal && <Total dueLabel={dueLabel} due={due} discount={discount} />}
    </>
  );
}

/**
 * Wider screens: the sticky summary beside the form — the visit's lines and
 * each guest card with its state, then what is due now and the actions (the
 * hold timer, the one primary button and Back: children). The column is
 * never taller than the scroller: when the lines and cards do not fit, they
 * scroll inside it, and the amount due and the actions stay on screen.
 */
export function SummaryAside(props: {
  lines: readonly SummaryLine[];
  riders: readonly SummaryRider[];
  noun: GuestNoun;
  dueLabel: string;
  due: string;
  discount?: SummaryDiscount | null;
  children?: ReactNode;
}) {
  const { children, ...details } = props;
  return (
    <aside className="vt-checkout-summary" aria-label="Summary">
      <div className="vt-checkout-summary-details">
        <p className="vt-checkout-summary-heading">Summary</p>
        <Details {...details} withTotal={false} />
      </div>
      <div className="vt-checkout-summary-actions">
        <Total dueLabel={details.dueLabel} due={details.due} discount={details.discount} />
        {children}
      </div>
    </aside>
  );
}

/**
 * A phone: one line at the top ("ZAR 3,800 · 4 riders · Thu 08:30") that
 * opens into the same summary.
 */
export function SummaryBar(props: {
  lines: readonly SummaryLine[];
  riders: readonly SummaryRider[];
  noun: GuestNoun;
  dueLabel: string;
  due: string;
  discount?: SummaryDiscount | null;
  when: string | null;
  /** When this changes (a step or the card on screen), the summary folds away again. */
  closeKey?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  useEffect(() => {
    setOpen(false);
  }, [props.closeKey]);
  const text = summaryBarText({ due: props.due, riders: props.riders.length, noun: props.noun, when: props.when });
  return (
    <div className="vt-checkout-summarybar" data-open={open ? "true" : "false"}>
      <button
        type="button"
        className="vt-checkout-summarybar-toggle"
        aria-expanded={open}
        aria-controls={id}
        data-hold-passive
        onClick={() => setOpen((v) => !v)}
      >
        <span className="vt-checkout-summarybar-text">{text}</span>
        <span className="vt-checkout-summarybar-action">{open ? "Hide" : "Details"}</span>
      </button>
      <div id={id} className="vt-checkout-summarybar-panel" hidden={!open}>
        <Details
          lines={props.lines}
          riders={props.riders}
          noun={props.noun}
          dueLabel={props.dueLabel}
          due={props.due}
          discount={props.discount}
        />
      </div>
    </div>
  );
}
